import { InteractionRequiredAuthError, IPublicClientApplication, SilentRequest } from "@azure/msal-browser";

type TokenRefreshConfiguration = {
  apiPermissionScopes: string[];
};

/**
 * TokenRefreshManager handles MSAL token refresh with request queuing.
 * Ensures only one token refresh happens at a time and queues requests during refresh.
 */
export class TokenRefreshManager {
  private static instance: TokenRefreshManager;
  private refreshPromise: Promise<string | null> | null = null;

  private constructor(
    private readonly msalInstance: IPublicClientApplication,
    private readonly tokenRefreshConfiguration: TokenRefreshConfiguration,
  ) {}

  /**
   * Initialize the TokenRefreshManager singleton
   */
  public static initialize(
    msalInstance: IPublicClientApplication,
    tokenRefreshConfiguration: TokenRefreshConfiguration,
  ): void {
    if (!TokenRefreshManager.instance) {
      TokenRefreshManager.instance = new TokenRefreshManager(msalInstance, tokenRefreshConfiguration);
    }
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): TokenRefreshManager {
    if (!TokenRefreshManager.instance) {
      throw new Error(
        "TokenRefreshManager not initialized. Call TokenRefreshManager.initialize() first in your auth provider.",
      );
    }

    return TokenRefreshManager.instance;
  }

  /**
   * Silently refresh the access token or return the existing promise if a refresh is in progress.
   * Returns the new access token or null if refresh failed.
   */
  public async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const account = this.getActiveAccount();

        if (!account) {
          console.warn("TokenRefreshManager: No active account found for token refresh");
          return null;
        }

        const request: SilentRequest = {
          scopes: this.tokenRefreshConfiguration.apiPermissionScopes,
          account,
        };

        const response = await this.msalInstance.acquireTokenSilent(request);
        console.debug("TokenRefreshManager: Access token refreshed silently");
        return response.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          console.error("TokenRefreshManager: Silent refresh failed - InteractionRequired. Trigger interactive login.");
          throw error;
        }

        console.error("TokenRefreshManager: Silent refresh failed", error);
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Trigger interactive token acquisition as a fallback when silent refresh fails
   */
  public async acquireTokenInteractive(): Promise<string | null> {
    try {
      const account = this.getActiveAccount();

      if (!account) {
        console.warn("TokenRefreshManager: No active account found for interactive token acquisition");
        return null;
      }

      const request: SilentRequest = {
        scopes: this.tokenRefreshConfiguration.apiPermissionScopes,
        account,
      };

      const response = await this.msalInstance.acquireTokenPopup(request);
      console.debug("TokenRefreshManager: Access token acquired via popup");
      return response.accessToken;
    } catch (error) {
      console.error("TokenRefreshManager: Interactive token acquisition failed", error);
      return null;
    }
  }

  /**
   * Trigger redirect-based token acquisition as an absolute fallback when popup fails.
   */
  public async acquireTokenRedirectFallback(): Promise<void> {
    try {
      const account = this.getActiveAccount();

      if (!account) {
        console.warn("TokenRefreshManager: No active account found for redirect token acquisition");
        return;
      }

      const request: SilentRequest = {
        scopes: this.tokenRefreshConfiguration.apiPermissionScopes,
        account,
      };

      await this.msalInstance.acquireTokenRedirect(request);
    } catch (error) {
      console.error("TokenRefreshManager: Redirect token acquisition failed", error);
    }
  }

  private getActiveAccount() {
    const account = this.msalInstance.getActiveAccount() ?? this.msalInstance.getAllAccounts()[0] ?? null;
    if (account) {
      this.msalInstance.setActiveAccount(account);
    }

    return account;
  }
}
